import { Box } from "@mui/material";
import { Controller } from "react-hook-form";
import {
  Button,
  Input,
  MultiSelect,
  Select,
  Textarea,
} from "../../../components";
import FileUploadField from "../../../components/fileUploadInput/FileUploadInput";
import { uploadOrgImage_V2 } from "../../../Services_V2/Organization.service";
import { BANK_ACCOUNT_TYPE_OPTIONS } from "../../AddEditNGOPage/const";
import styles from "../CreateEditOrganization/CreateEditOrganization.module.css";
import { RegisterOrgInputs } from "./const";

interface OrganizationFormProps {
  isEdit: boolean;
  control: any;
  register: any;
  errors: any;
  cityOptions: { label: string; value: string }[];
  categoryOptions: { label: string; value: string }[];
  logoPreview: string | null;
  cert80GPreview: string | null;
  trustDeedPreview: string | null;
  donationQrPreview: string | null;
  setLogoPreview: (url: string | null) => void;
  setCert80GPreview: (url: string | null) => void;
  setTrustDeedPreview: (url: string | null) => void;
  setDonationQrPreview: (url: string | null) => void;
  resolvedOrgId?: string;
  onSubmit: (data: any) => void;
  loading: boolean;
}

const OrganizationForm: React.FC<OrganizationFormProps> = ({
  isEdit,
  control,
  register,
  errors,
  cityOptions,
  categoryOptions,
  logoPreview,
  cert80GPreview,
  trustDeedPreview,
  donationQrPreview,
  setLogoPreview,
  setCert80GPreview,
  setTrustDeedPreview,
  setDonationQrPreview,
  resolvedOrgId,
  onSubmit,
  loading,
}) => {
  return (
    <Box className={styles.container}>
      <form className={styles.form} onSubmit={onSubmit}>
        <h2 className="text-center">
          {isEdit ? "Update" : "Create"} Organization
        </h2>

        <Box className={styles.sections}>
          <Box sx={{ flex: 1 }}>
            <h3>Organization Details</h3>
            <Box className={styles.orgInputsContainer}>
              <Input
                fullWidth
                label="Organization Name *"
                placeholder="Enter name"
                {...register("name")}
                error={!!errors.name}
                helperText={errors.name?.message}
              />
              <Input
                fullWidth
                label="Organization Title *"
                placeholder="Enter title"
                {...register("title")}
                error={!!errors.title}
                helperText={errors.title?.message}
              />
              <Input
                fullWidth
                label="Tagline *"
                placeholder="Enter tagline"
                {...register("tagline")}
                error={!!errors.tagline}
                helperText={errors.tagline?.message}
              />
              <Input
                fullWidth
                label="Organization Email *"
                placeholder="org@example.com"
                {...register("email")}
                error={!!errors.email}
                helperText={errors.email?.message}
              />
              <Input
                fullWidth
                label="Website URL"
                placeholder="https://example.com"
                {...register("websiteUrl")}
                error={!!errors.websiteUrl}
                helperText={errors.websiteUrl?.message}
              />
              <Input
                fullWidth
                label="Darpan ID *"
                placeholder="Enter Darpan ID"
                {...register("darpanId")}
                error={!!errors.darpanId}
                helperText={errors.darpanId?.message}
              />

              <Controller
                name="cities"
                control={control}
                render={({ field }) => (
                  <MultiSelect
                    label="Cities *"
                    placeholder="Add Cities"
                    options={cityOptions}
                    value={cityOptions.filter((o) =>
                      field.value?.includes(o.value)
                    )}
                    onValueChange={(selected) =>
                      field.onChange(selected.map((o) => o.value))
                    }
                    error={!!errors.cities}
                    fullWidth
                    helperText={errors.cities?.message}
                  />
                )}
              />

              <Controller
                name="categories"
                control={control}
                render={({ field }) => (
                  <MultiSelect
                    label="Categories"
                    placeholder="Select Categories"
                    options={categoryOptions}
                    value={categoryOptions.filter((o) =>
                      field.value?.includes(o.value)
                    )}
                    onValueChange={(selected) =>
                      field.onChange(selected.map((o) => o.value))
                    }
                    error={!!errors.categories}
                    fullWidth
                    helperText={errors.categories?.message}
                  />
                )}
              />

              <Textarea
                fullWidth
                label="Description *"
                placeholder="Enter description"
                rows={4}
                {...register("description")}
                error={!!errors.description}
                helperText={errors.description?.message}
              />

              <div className={styles.sectionTitle}>
                Bank Account Details (for Donations)
              </div>

              <Box sx={{ display: "flex", gap: 2, flexDirection: "column" }}>
                <Input
                  label="Bank Name *"
                  placeholder="State Bank of India"
                  fullWidth
                  {...register("bankDetails.bankName")}
                  error={!!errors.bankDetails?.bankName}
                  helperText={errors.bankDetails?.bankName?.message}
                />
                <Input
                  label="Bank Branch *"
                  placeholder="Main Branch"
                  fullWidth
                  {...register("bankDetails.branch")}
                  error={!!errors.bankDetails?.branch}
                  helperText={errors.bankDetails?.branch?.message}
                />
                <Input
                  label="IFSC Code *"
                  placeholder="SBIN0001234"
                  fullWidth
                  {...register("bankDetails.ifscCode")}
                  error={!!errors.bankDetails?.ifscCode}
                  helperText={errors.bankDetails?.ifscCode?.message}
                />

                <Controller
                  name="bankDetails.accountType"
                  control={control}
                  render={({ field }) => (
                    <Select
                      label="Account Type *"
                      options={BANK_ACCOUNT_TYPE_OPTIONS}
                      fullWidth
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      error={!!errors.bankDetails?.accountType}
                      helperText={errors.bankDetails?.accountType?.message}
                    />
                  )}
                />

                <Input
                  label="Beneficiary/Account Name *"
                  placeholder="Name as in bank account"
                  fullWidth
                  {...register("bankDetails.beneficiaryName")}
                  error={!!errors.bankDetails?.beneficiaryName}
                  helperText={errors.bankDetails?.beneficiaryName?.message}
                />
                <Input
                  label="Account Number *"
                  placeholder="Enter account number"
                  fullWidth
                  {...register("bankDetails.accountNumber")}
                  error={!!errors.bankDetails?.accountNumber}
                  helperText={errors.bankDetails?.accountNumber?.message}
                />
                <Input
                  label="UPI ID (Optional)"
                  placeholder="ngo.name@upi"
                  fullWidth
                  {...register("bankDetails.upiId")}
                  error={!!errors.bankDetails?.upiId}
                  helperText={errors.bankDetails?.upiId?.message}
                />
              </Box>

              <FileUploadField
                name="donationQr"
                control={control}
                errors={errors}
                label={isEdit ? "Update Donation QR" : "Upload Donation QR *"}
                fieldabel="Donation QR"
                subText="Upload UPI QR code (JPG, PNG)"
                accept="image/*"
                previewUrl={donationQrPreview}
                onPreviewChange={setDonationQrPreview}
                entityId={resolvedOrgId}
                onUpload={uploadOrgImage_V2}
                fieldKey="donationQr"
                isEdit={isEdit}
              />

              <FileUploadField
                name={RegisterOrgInputs.LOGO}
                control={control}
                errors={errors}
                label={
                  isEdit
                    ? "Upload Organization Logo"
                    : "Upload Organization Logo *"
                }
                fieldabel="Organization Logo"
                subText="Click to upload image (JPG, PNG, etc.)"
                accept="image/*"
                previewUrl={logoPreview}
                onPreviewChange={setLogoPreview}
                entityId={resolvedOrgId}
                onUpload={uploadOrgImage_V2}
                fieldKey="logo"
                isEdit={isEdit}
              />

              <FileUploadField
                name={RegisterOrgInputs.EIGHTY_G_CERTIFICATE}
                control={control}
                errors={errors}
                label={isEdit ? "Upload 80G" : "Upload 80G *"}
                fieldabel="80G Certificate"
                subText="PDF or Image"
                accept="image/*,application/pdf"
                previewUrl={cert80GPreview}
                onPreviewChange={setCert80GPreview}
                entityId={resolvedOrgId}
                onUpload={uploadOrgImage_V2}
                fieldKey="eightyGCertificate"
                isEdit={isEdit}
              />

              <FileUploadField
                name={RegisterOrgInputs.TRUST_DEED_CERTIFICATE}
                control={control}
                errors={errors}
                label={isEdit ? "Upload Trust Deed" : "Upload Trust Deed *"}
                fieldabel="Trust Deed"
                subText="PDF or Image"
                accept="image/*,application/pdf"
                previewUrl={trustDeedPreview}
                onPreviewChange={setTrustDeedPreview}
                entityId={resolvedOrgId}
                onUpload={uploadOrgImage_V2}
                fieldKey="trustDeedCertificate"
                isEdit={isEdit}
              />
            </Box>
          </Box>

          {!isEdit && (
            <Box sx={{ flex: 1 }}>
              <h3>Contact Person Details</h3>
              <Box className={styles.contactPersonsContanier}>
                <Input
                  fullWidth
                  label="Contact Person Name *"
                  placeholder="Enter name"
                  {...register("contact_person.name")}
                  error={!!errors.contact_person?.name}
                  helperText={errors.contact_person?.name?.message}
                />
                <Input
                  fullWidth
                  label="Contact Person Email *"
                  placeholder="contact@example.com"
                  {...register("contact_person.email")}
                  error={!!errors.contact_person?.email}
                  helperText={errors.contact_person?.email?.message}
                />
                <Input
                  fullWidth
                  type="number"
                  label="Contact Person Phone"
                  placeholder="9876543210"
                  {...register("contact_person.number")}
                  error={!!errors.contact_person?.number}
                  helperText={errors.contact_person?.number?.message}
                />
              </Box>
            </Box>
          )}
        </Box>

        <Button type="submit" fullWidth sx={{ mt: 4 }} loading={loading}>
          {isEdit ? "Update" : "Create"} Organization
        </Button>
      </form>
    </Box>
  );
};

export default OrganizationForm;
